import React from "react";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { TextField } from "@remo-co/ui-core/src/components/TextField";
import {
  MIN_BREAK_DURATION_MINUTES,
  MAX_BREAK_DURATION_MINUTES,
  MIN_TIMER_DURATION_MINUTES,
  MAX_TIMER_DURATION_MINUTES,
  MIN_SESSIONS,
  MAX_SESSIONS,
  isValidBreakLength,
  isValidSessionLength,
  isValidSessionNum,
} from "./TimerHelpers";
import "./TimerSetup.scss";

// allow users to delete the input field by setting the value to empty string
interface ITimerSetup {
  minutes: string;
  rounds: string;
  bufferTime: string;
  onSet(
    roundsInput: string,
    minutesInput: string,
    bufferTimeInput: string,
  ): void;
}

const TimerSetup = ({ minutes, rounds, bufferTime, onSet }: ITimerSetup) => {
  const [minutesInput, setMinutesInput] = React.useState(minutes);
  const [roundsInput, setRoundsInput] = React.useState(rounds);
  const [bufferTimeInput, setBufferTimeInput] = React.useState(bufferTime); // in min
  const [errorMessage, setErrorMessage] = React.useState("");
  const { t } = useI18n(["event"]);

  const handleMinutesInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      setMinutesInput(e.target.value);
      setErrorMessage("");
    },
    [],
  );

  React.useEffect(() => {
    setMinutesInput(minutes);
  }, [minutes]);

  React.useEffect(() => {
    setRoundsInput(rounds);
  }, [rounds]);

  React.useEffect(() => {
    setBufferTimeInput(bufferTime);
  }, [bufferTime]);

  const handleRoundsInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      setRoundsInput(e.target.value);
      setErrorMessage("");
    },
    [],
  );

  const handleBufferInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      setBufferTimeInput(e.target.value);
      setErrorMessage("");
    },
    [],
  );

  // eslint-disable-next-line consistent-return
  const handleSet = React.useCallback(() => {
    if (!isValidSessionNum(+roundsInput)) {
      return setErrorMessage(
        t("event:number.session.error", {
          min: MIN_SESSIONS,
          max: MAX_SESSIONS,
        }),
      );
    }

    if (!isValidBreakLength(+bufferTimeInput)) {
      return setErrorMessage(
        t("event:number.buffer.error", {
          min: MIN_BREAK_DURATION_MINUTES,
          max: MAX_BREAK_DURATION_MINUTES,
        }),
      );
    }

    if (!isValidSessionLength(+minutesInput)) {
      return setErrorMessage(
        t("event:number.minutes.error", {
          min: MIN_TIMER_DURATION_MINUTES,
          max: MAX_TIMER_DURATION_MINUTES,
        }),
      );
    }
    onSet(roundsInput, minutesInput, bufferTimeInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundsInput, minutesInput, bufferTimeInput]);

  return (
    <div className="timer-setup-ctn" data-testid="timer-setup-container">
      <div className="line">
        <span className="input-label">
          <Typography>{t("event:number.session")}</Typography>
        </span>
        <TextField
          id="session-number"
          data-testid="timer-setup-session-number"
          error={!!errorMessage && !isValidSessionNum(+roundsInput)}
          value={roundsInput}
          onChange={handleRoundsInputChange}
          type="number"
          name="roundsInput"
          className="number-field"
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          InputProps={{ inputProps: { min: MIN_SESSIONS, max: MAX_SESSIONS } }}
        />
      </div>
      <div className="line">
        <span className="input-label">
          <Typography>{t("event:duration.each.session")}</Typography>
        </span>

        <TextField
          id="session-duration"
          data-testid="timer-setup-session-duration"
          value={minutesInput}
          error={!!errorMessage && !isValidSessionLength(+minutesInput)}
          onChange={handleMinutesInputChange}
          type="number"
          name="minutesInput"
          className="number-field"
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          InputProps={{
            inputProps: {
              min: MIN_TIMER_DURATION_MINUTES,
              max: MAX_TIMER_DURATION_MINUTES,
            },
          }}
        />
      </div>
      <div className="line">
        <span className="input-label">
          <Typography>{t("event:breaks.each.session")}</Typography>
        </span>

        <TextField
          id="session-break"
          data-testid="timer-setup-session-break"
          value={bufferTimeInput}
          error={!!errorMessage && !isValidBreakLength(+bufferTimeInput)}
          onChange={handleBufferInputChange}
          type="number"
          name="bufferTimeInput"
          className="number-field"
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          InputProps={{
            inputProps: {
              min: MIN_BREAK_DURATION_MINUTES,
              max: MAX_BREAK_DURATION_MINUTES,
            },
          }}
        />
      </div>
      <div>
        {errorMessage && (
          <span className="input-label error">
            <Typography>{errorMessage}</Typography>
          </span>
        )}
        <Button
          onClick={handleSet}
          className="reset-btn set-btn"
          variant="primary"
          color="blue"
          size="sm"
          data-testid="timer-setup-button"
        >
          {t("event:set.timer")}
        </Button>
      </div>
    </div>
  );
};

export default TimerSetup;
